import React, { useEffect, useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  checkUsernameAvailable,
  loginUser,
  LoginLocationState,
  registerUser,
  saveAuthToken,
  startGoogleSignIn,
} from '../api/auth'
import { GutstringBrand } from './GutstringBrand'
import './RegisterPage.css'

const SUCCESS_REDIRECT_MS = 2800
const USERNAME_CHECK_MS = 400
const USERNAME_RE = /^[a-z0-9_]{3,30}$/

function AuthLanding() {
  return (
    <>
      <Link to="sign-up" className="register-btn">
        Register
      </Link>
      <Link to="/login" className="register-btn register-btn--outline">
        Login
      </Link>
    </>
  )
}

type AuthFormMode = 'signup' | 'login'
type UsernameStatus = 'idle' | 'checking' | 'available' | 'taken'

function normalizeUsername(raw: string): string {
  return raw.replace(/^@+/, '').toLowerCase().replace(/[^a-z0-9_]/g, '')
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  )
}

function AuthForm({ mode }: { mode: AuthFormMode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const loginState = (location.state as LoginLocationState | null) ?? {}
  const [sessionNotice, setSessionNotice] = useState(
    () => (mode === 'login' ? loginState.message?.trim() ?? '' : ''),
  )
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle')
  const usernameCheckId = useRef(0)

  const isSignup = mode === 'signup'

  useEffect(() => {
    if (mode !== 'login' || !loginState.message) return
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.pathname, loginState.message, mode, navigate])

  useEffect(() => {
    if (!isSignup) return
    if (!USERNAME_RE.test(username)) {
      setUsernameStatus('idle')
      return
    }
    const id = ++usernameCheckId.current
    setUsernameStatus('checking')
    const timer = window.setTimeout(async () => {
      try {
        const available = await checkUsernameAvailable(username)
        if (id !== usernameCheckId.current) return
        setUsernameStatus(available ? 'available' : 'taken')
      } catch {
        if (id !== usernameCheckId.current) return
        setUsernameStatus('idle')
      }
    }, USERNAME_CHECK_MS)
    return () => {
      usernameCheckId.current += 1
      window.clearTimeout(timer)
    }
  }, [username, isSignup])

  function validate(): boolean {
    setError('')
    if (isSignup) {
      const normalizedUsername = normalizeUsername(username)
      if (!normalizedUsername || normalizedUsername.length < 3) {
        setError('Username must be at least 3 characters (letters, numbers, underscore).')
        return false
      }
      if (normalizedUsername.length > 30) {
        setError('Username must be 30 characters or fewer.')
        return false
      }
      if (usernameStatus === 'checking') {
        setError('Still checking username availability.')
        return false
      }
      if (usernameStatus === 'taken') {
        setError('This username is already taken.')
        return false
      }
      if (displayName.trim().length > 50) {
        setError('Display name must be 50 characters or fewer.')
        return false
      }
    }
    if (isSignup) {
      if (!email || !email.includes('@') || !email.includes('.')) {
        setError('Please enter a valid email address.')
        return false
      }
    } else if (!loginId.trim()) {
      setError('Enter your email or username.')
      return false
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters.')
      return false
    }
    return true
  }

  async function handleSubmit() {
    if (!validate()) return
    setSubmitting(true)
    setError('')
    setSessionNotice('')
    try {
      if (isSignup) {
        const normalizedUsername = normalizeUsername(username)
        const data = await registerUser({
          email: email.trim(),
          password,
          username: normalizedUsername,
          displayName: displayName.trim() || undefined,
        })
        saveAuthToken(data.access_token)
        setSuccess(true)
      } else {
        const data = await loginUser({
          login: loginId.includes('@')
            ? loginId.trim()
            : normalizeUsername(loginId),
          password,
        })
        saveAuthToken(data.access_token)
        navigate('/')
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (!success || !isSignup) return
    const timer = window.setTimeout(() => navigate('/'), SUCCESS_REDIRECT_MS)
    return () => window.clearTimeout(timer)
  }, [success, isSignup, navigate])

  function handleGoogle() {
    setError('')
    startGoogleSignIn()
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleSubmit()
  }

  const usernameTaken = usernameStatus === 'taken'
  const usernameChecking = usernameStatus === 'checking'
  const usernameHint =
    usernameStatus === 'taken'
      ? 'This username is already taken.'
      : usernameStatus === 'checking'
        ? 'Checking availability…'
        : usernameStatus === 'available'
          ? 'Username is available.'
          : 'Your username cannot be changed later.'
  const usernameHintClass =
    usernameStatus === 'taken'
      ? 'register-field-hint register-field-hint--taken'
      : usernameStatus === 'checking'
        ? 'register-field-hint register-field-hint--warn'
        : usernameStatus === 'available'
          ? 'register-field-hint register-field-hint--ok'
          : 'register-field-hint'

  const signupBlocked =
    isSignup &&
    USERNAME_RE.test(username) &&
    (usernameChecking || usernameTaken)

  if (success && isSignup) {
    return (
      <div className="register-success">
        <div className="register-success-check">✓</div>
        <div className="register-success-title">You're In</div>
        <div className="register-success-subtitle">
          Account created — taking you in now.
        </div>
      </div>
    )
  }

  const swapLink =
    mode === 'signup' ? (
      <p className="register-auth-switch">
        Already have an account?{' '}
        <Link to="/login" className="register-auth-switch-link">
          Log in
        </Link>
      </p>
    ) : (
      <p className="register-auth-switch">
        Need an account?{' '}
        <Link to="/register/sign-up" className="register-auth-switch-link">
          Register
        </Link>
      </p>
    )

  return (
    <div className="register-auth-form">
      {sessionNotice && (
        <p className="register-field-hint register-field-hint--warn register-session-notice">
          {sessionNotice}
        </p>
      )}
      <div className="register-email-area">
        {isSignup && (
          <>
            <input
              className={`register-email-input${error || usernameTaken ? ' error' : ''}`}
              type="text"
              placeholder="Username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(normalizeUsername(e.target.value))}
              onKeyDown={handleKeyDown}
              aria-invalid={usernameTaken}
            />
            <p className={usernameHintClass}>{usernameHint}</p>
            <input
              className={`register-email-input${error ? ' error' : ''}`}
              type="text"
              placeholder="Display name"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </>
        )}
        {isSignup ? (
          <input
            className={`register-email-input${error ? ' error' : ''}`}
            type="email"
            placeholder="your@email.com"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <input
            className={`register-email-input${error ? ' error' : ''}`}
            type="text"
            placeholder="Email or username"
            autoComplete="username"
            value={loginId}
            onChange={(e) =>
              setLoginId(
                e.target.value.includes('@')
                  ? e.target.value
                  : normalizeUsername(e.target.value),
              )
            }
            onKeyDown={handleKeyDown}
          />
        )}
        <input
          className={`register-email-input${error ? ' error' : ''}`}
          type="password"
          placeholder="Password"
          autoComplete={isSignup ? 'new-password' : 'current-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="register-submit-btn"
          onClick={handleSubmit}
          disabled={submitting || signupBlocked}
        >
          {submitting
            ? 'Please wait…'
            : usernameChecking && isSignup
              ? 'Checking username…'
              : isSignup
                ? 'Create account'
                : 'Log in'}
        </button>
        {error && <p className="register-error-msg">{error}</p>}
      </div>

      <p className="register-auth-divider">
        <span>or</span>
      </p>

      <button
        type="button"
        className="register-btn register-btn--outline register-google-btn"
        onClick={handleGoogle}
      >
        <GoogleMark />
        <span>Continue with Google</span>
      </button>

      {swapLink}
    </div>
  )
}

export function RegisterPage() {
  return (
    <div className="register-root">
      <div className="register-grain" aria-hidden="true" />

      <div className="register-blob register-blob-1" aria-hidden="true" />
      <div className="register-blob register-blob-2" aria-hidden="true" />

      <div className="register-page">
        <header className="register-top-bar">
          <GutstringBrand variant="light" linkToHome className="register-top-brand" />
          <div className="register-top-badge">Early Access</div>
        </header>

        <main className="register-hero">
          <div className="register-marketing">
            <div className="register-eyebrow">
              <span className="register-dot" />
              <span>Live · By Asycd</span>
            </div>

            <h1 className="register-headline">
              <span className="register-line register-line-1">For the</span>
              <span className="register-line register-line-2">unfinished</span>
              <span className="register-line register-line-3">ideas.</span>
            </h1>

            <p className="register-description">
              Naked opinions. Raw art. Unsolicited expression. Gutstring is a new
              home for those gut feelings that deserve more than a quick death on
              social media.
            </p>
          </div>

          <div className="register-panel">
            <div className="register-area">
              <Outlet />
            </div>

            <p className="register-fine-print">No spam. Unsubscribe at any time.</p>
          </div>
        </main>

        <footer className="register-footer">
          <span>© 2026 Asycd · Gutstring</span>
          <span className="register-footer-sep">·</span>
          <a href="https://www.asycd.online/privacy" rel="noopener noreferrer">
            Privacy
          </a>
          <span className="register-footer-sep">·</span>
          <a href="https://www.asycd.online/contact" rel="noopener noreferrer">
            Contact
          </a>
        </footer>
      </div>
    </div>
  )
}

export function RegisterLanding() {
  return <AuthLanding />
}

export function RegisterAuthFormSignup() {
  return <AuthForm mode="signup" />
}

export function RegisterAuthFormLogin() {
  return <AuthForm mode="login" />
}
